import { Box, Button, IconButton, Stack, SxProps, TextField, Typography } from '@mui/material';
import { abyssColors } from '@/theme/colors';
import * as kpiState from '../state';
import { useAtomValue } from 'jotai';
import {
  AllCommentsGenericDocument,
  useAllCommentsGenericQuery,
  useCreateCommentMutation,
  useUpdateCommentMutation,
} from '@/__generated__/graphql';
import { useMemo, useState } from 'react';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { extractErrorMessageFromUnknownError } from '@/utils/error';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { Banner } from '@/components/shared/Banner';
import { COLLECTION_NAMES } from '@/constants';
import {
  COMMENT_METADATA_TYPE_KPI_LOG_NOTE,
  KPI_NOTE_LOG_METADATA_KEY,
  KPI_NOTE_LOG_SHIFT_METADATA_KEY,
} from '../constants';
import { isDate } from 'date-fns';
import { formatDateToYYYYMMDD, isSameCalendarDay } from '../utils';

type Props = {
  helperText: string | undefined;
  sx?: SxProps;
  sectionKey: string;
  isError: boolean;
  isPobEmpty: boolean;
};

export const DiscrepancyReasonField = ({ helperText, sx, sectionKey, isError }: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const selectedWorkpackIdForKpiTracker = useAtomValue(kpiState.selectedWorkpackIdForKpiTracker);
  const selectedDate = useAtomValue(kpiState.selectedDate);
  const selectedShift = useAtomValue(kpiState.selectedShift);
  const key = `${sectionKey}.discrepancy_reason`;

  const structureId = useGetStructureId();
  const { showSnackBar } = useSnackBarMessage({});

  const [createComment, { loading: createLoading }] = useCreateCommentMutation({
    awaitRefetchQueries: true,
    refetchQueries: [AllCommentsGenericDocument],
  });

  const [updateComment, { loading: updateLoading }] = useUpdateCommentMutation({
    awaitRefetchQueries: true,
    refetchQueries: [AllCommentsGenericDocument],
  });

  const { data: commentsData } = useAllCommentsGenericQuery({
    variables: {
      input: {
        collectionName: COLLECTION_NAMES.WORKPACK,
        referenceId: selectedWorkpackIdForKpiTracker as string,
        structureId,
      },
    },
    skip: !selectedWorkpackIdForKpiTracker || !structureId,
  });

  const comment = useMemo(() => {
    if (!commentsData?.allComments) return;

    // Filter by todays date and shift
    const todayComments = commentsData.allComments.filter((comment) => {
      const date = new Date(comment?.metadata?.[KPI_NOTE_LOG_METADATA_KEY]);
      if (!isDate(date)) return false;
      const isSameDate = isSameCalendarDay(date, selectedDate);
      const commentShift = comment?.metadata?.[KPI_NOTE_LOG_SHIFT_METADATA_KEY];
      const isSameShift = commentShift === selectedShift;
      return isSameDate && isSameShift;
    });

    return todayComments.find((comment) => comment.key === key);
  }, [commentsData?.allComments, key, selectedDate, selectedShift]);

  const hasCommentText = Boolean(comment?.text?.trim());

  const [value, setValue] = useState(() => {
    return comment?.text || '';
  });

  const onSave = async () => {
    try {
      if (isEditing) {
        if (!comment) throw new Error('Comment is not available');
        await updateComment({
          variables: {
            input: {
              id: comment.id,
              text: value,
              structureId,
            },
          },
        });
      } else {
        if (!selectedWorkpackIdForKpiTracker) throw new Error('Workpack ID is not available');
        await createComment({
          variables: {
            input: {
              collectionName: COLLECTION_NAMES.WORKPACK,
              referenceId: selectedWorkpackIdForKpiTracker,
              structureId,
              text: value,
              key,
              metadata: {
                [KPI_NOTE_LOG_METADATA_KEY]: formatDateToYYYYMMDD(selectedDate),
                [KPI_NOTE_LOG_SHIFT_METADATA_KEY]: selectedShift,
                type: COMMENT_METADATA_TYPE_KPI_LOG_NOTE,
              },
            },
          },
        });
      }

      setValue('');
      setIsEditing(false);
    } catch (error: unknown) {
      const message = extractErrorMessageFromUnknownError(error);
      showSnackBar(message, 'error');
    }
  };

  const onCancel = () => {
    if (comment) {
      setValue(comment.text);
    }
    setIsEditing(false);
  };

  const ActionButtons = () => {
    const getButtonLabel = () => {
      if (!isEditing) return 'Add Reason';
      return value.trim() ? 'Update Reason' : 'Save Reason';
    };

    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        {isEditing && (
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={createLoading || updateLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
        )}
        <Button
          id="kpi-tracker-discrepancy-reason-save-button"
          variant="contained"
          onClick={onSave}
          disabled={(!isEditing && !value.trim()) || createLoading || updateLoading}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
          }}
          color="primary"
        >
          {getButtonLabel()}
        </Button>
      </Box>
    );
  };

  const View = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          border: '1px solid',
          borderColor: abyssColors.primary[200],
          borderRadius: 1,
          p: 1,
          my: 1,
        }}
      >
        <Box sx={{ width: '95%', minWidth: 0, pr: 1 }}>
          <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {comment?.text}
          </Typography>
        </Box>
        <Box>
          <IconButton
            onClick={() => {
              setIsEditing(true);
              setValue(comment?.text || '');
            }}
          >
            <BorderColorIcon />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <Stack spacing={2} sx={sx}>
      {/* Only show in case of error */}
      {isError && <Banner message={helperText || ''} severity="warning" />}
      {/* 1 - Comment nullish check
        2 - Check is user is not editing the comment
        3 - Check if its the same user the created the comment */}
      {comment && hasCommentText && !isEditing && <View />}

      {/* 1- Only show if theres an error */}
      {/* 2 - Show if theres no comment i.e add new comment */}
      {/* 3 - Show if user is editing the comment */}
      {(isEditing || (isError && !hasCommentText)) && (
        <TextField
          multiline
          rows={4}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={createLoading || updateLoading}
          placeholder="Add a reason here..."
        />
      )}

      {/* Same conditions as above */}
      {/* Since this needs to be shown whenever the user is editing or adding a new comment */}
      {(isEditing || (isError && !hasCommentText)) && <ActionButtons />}
    </Stack>
  );
};
